import {
  AcknowledgeNotificationRequestSchema,
  ENDPOINTS,
  NotificationListRequestSchema,
  type AcknowledgeNotificationRequest,
  type AcknowledgeNotificationResponse,
  type NotificationListRequest,
  type NotificationListResponse,
} from '@clm/shared-types';

import { apiGet, apiPost, type Query } from './client';

/**
 * `notifications` — the read side of the §3 ladder.
 *
 * Needs attention is built on this endpoint rather than on a browser-side
 * fusion of a verification queue and an expiring-soon contract search. The
 * reason is O-1: *"ordered by what lapses first"* is tier arithmetic, and the
 * tier arithmetic belongs with the notification rules, server-side, where the
 * `(rule, contract, occurrence_date, tier)` idempotency key lives. A browser
 * ranking two unrelated result sets against each other would produce two
 * different scope snapshots and a total that matches neither.
 *
 * All query parameters, same encoding as the verification queue: dotted keys
 * for the nested page object, repeated keys for arrays, `true`/`false` for
 * booleans (`apps/api/src/http/decode.ts`).
 *
 * NOTE on what answers. CLM-28 replaced the `notImplemented` handlers with
 * `runNotificationList` and `runAcknowledgeNotification`
 * (`apps/api/src/notification/*`), so on a develop API with a database both
 * endpoints answer for real. Two honest non-answers remain first-class states
 * for the surfaces, not errors: a develop API built before CLM-28 still answers
 * `501` (`not_implemented`), and production has no API at all until `deploy-api`
 * is enabled (B-33), so the first call there rejects at network level
 * (`unknown`). Needs attention and the Notifications ledger both name
 * `GET /v1/notifications` in those states rather than presenting an empty ladder.
 */
const NOTIFICATIONS_ENDPOINT = ENDPOINTS.notifications;

export function notificationListQuery(request: NotificationListRequest): Query {
  const query: Record<string, string | number | boolean | readonly string[]> = {};

  if (request.contract_key !== undefined) query['contract_key'] = request.contract_key;
  if (request.party_key !== undefined) query['party_key'] = request.party_key;
  if (request.watch_type !== undefined) query['watch_type'] = [...request.watch_type];
  if (request.tier !== undefined) query['tier'] = [...request.tier];
  if (request.include_acknowledged !== undefined) {
    query['include_acknowledged'] = request.include_acknowledged;
  }
  if (request.page?.limit !== undefined) query['page.limit'] = request.page.limit;
  if (request.page?.cursor !== undefined) query['page.cursor'] = request.page.cursor;

  return query;
}

export async function fetchNotifications(
  request: NotificationListRequest,
  signal?: AbortSignal,
): Promise<NotificationListResponse> {
  const validated = NotificationListRequestSchema.parse(request);
  const raw = await apiGet<unknown>(NOTIFICATIONS_ENDPOINT.path, {
    query: notificationListQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return NOTIFICATIONS_ENDPOINT.response.parse(raw);
}

/**
 * `acknowledge-notification` — the write side. O-1, FR-04, NFR-13.
 *
 * Addressed by the `(rule_id, contract_key, occurrence_date, tier)` idempotency
 * key, never by a surrogate id, so a double-click or a retried request resolves
 * to the same row and `was_already_acknowledged` reports which call got there
 * first — a replay is a `200`, not a conflict. The API writes exactly one
 * `clm.audit_log` row per first acknowledgement (NFR-13) and returns its receipt;
 * on a replay no row is written and `audit.audit_id` reads `replay:<correlation>`.
 *
 * The request is parsed through the strict contract schema before anything is
 * sent (an empty note or an unknown key is a ZodError here, not a 400 later),
 * and the response is parsed on the way back so an off-contract answer is a
 * classified `off_contract` state rather than a wrong render.
 */
const ACKNOWLEDGE_ENDPOINT = ENDPOINTS['acknowledge-notification'];

export async function acknowledgeNotification(
  request: AcknowledgeNotificationRequest,
  signal?: AbortSignal,
): Promise<AcknowledgeNotificationResponse> {
  const body = AcknowledgeNotificationRequestSchema.parse(request);
  const raw = await apiPost<unknown>(
    ACKNOWLEDGE_ENDPOINT.path,
    body,
    signal === undefined ? {} : { signal },
  );
  return ACKNOWLEDGE_ENDPOINT.response.parse(raw);
}
